import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabase';
import { Mail, Plus, Trash2, Copy, Check } from 'lucide-react';
import { useI18n, formatDateWithMonthName } from '../i18n';
import { Button, Card, EmptyState, IconButton, Input, Notice, SectionTitle } from './ui';

export default function InviteManager({ projetoId }) {
  const { t, lang } = useI18n();
  const [convites, setConvites] = useState([]);
  const [novoEmail, setNovoEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);

  const carregarConvites = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('convites')
        .select('*')
        .eq('projeto_id', projetoId)
        .eq('usado', false)
        .order('criado_em', { ascending: false });

      if (error) throw error;

      setConvites(data || []);
    } catch (error) {
      console.error('Error loading invites:', error);
    }
  }, [projetoId]);

  useEffect(() => {
    carregarConvites();
  }, [carregarConvites]);

  const showMessage = (text, error = false, timeout = 5000) => {
    setMessage(text);
    setIsError(error);
    if (timeout) setTimeout(() => setMessage(''), timeout);
  };

  const criarConvite = async () => {
    if (!novoEmail.trim()) {
      showMessage(t('inviteManager.invalidEmail'), true);
      return;
    }

    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailRegex.test(novoEmail.trim())) {
      showMessage(t('inviteManager.emailFormat'), true);
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const { data: session } = await supabase.auth.getSession();

      const { data, error } = await supabase
        .from('convites')
        .insert({
          projeto_id: projetoId,
          email: novoEmail.trim().toLowerCase(),
          convidado_por: session.session.user.id
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          showMessage(t('inviteManager.alreadyInvited'), true);
        } else {
          throw error;
        }
        return;
      }

      setConvites([data, ...convites]);
      setNovoEmail('');
      showMessage(t('inviteManager.inviteCreated'), false);
    } catch (error) {
      showMessage(t('inviteManager.createError', { message: error.message }), true);
    } finally {
      setLoading(false);
    }
  };

  const removerConvite = async (id) => {
    if (!window.confirm(t('inviteManager.confirmRemove'))) return;

    try {
      const { error } = await supabase
        .from('convites')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setConvites(convites.filter(c => c.id !== id));
      showMessage(t('inviteManager.inviteRemoved'), false, 3000);
    } catch (error) {
      showMessage(t('inviteManager.removeError', { message: error.message }), true);
    }
  };

  const copiar = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto);
      return true;
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      return false;
    }
  };

  const copiarToken = async (token) => {
    if (!(await copiar(token))) return;
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const copiarMensagemCompleta = async (email, token) => {
    const mensagem = t('inviteManager.shareMessage', { email, token });
    if (await copiar(mensagem)) showMessage(t('inviteManager.messageCopied'), false, 3000);
  };

  return (
    <div className="space-y-6">
      <div>
        <SectionTitle>{t('inviteManager.title')}</SectionTitle>
        <p className="text-sm text-gray-500 mb-3">{t('inviteManager.description')}</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            type="email"
            value={novoEmail}
            onChange={(e) => setNovoEmail(e.target.value)}
            placeholder={t('inviteManager.emailPlaceholder')}
            aria-label={t('inviteManager.emailPlaceholder')}
            onKeyDown={(e) => e.key === 'Enter' && criarConvite()}
          />
          <Button onClick={criarConvite} disabled={loading || !novoEmail.trim()} className="sm:flex-shrink-0">
            <Plus size={16} />{t('inviteManager.create')}
          </Button>
        </div>
        {message && (
          <Notice tone={isError ? 'error' : 'success'} className="mt-3">{message}</Notice>
        )}
      </div>

      <div>
        <SectionTitle>{t('inviteManager.pendingTitle', { count: convites.length })}</SectionTitle>
        {convites.length === 0 ? (
          <EmptyState icon={Mail} title={t('inviteManager.empty')} body={t('inviteManager.emptyHelp')} />
        ) : (
          <div className="space-y-2">
            {convites.map((convite) => (
              <Card key={convite.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 truncate">{convite.email}</div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="text-xs text-gray-500">{t('inviteManager.codeLabel')}</span>
                      <code className="px-2 py-1 rounded-md bg-gray-900 text-white text-xs font-semibold tracking-widest">{convite.token}</code>
                      <IconButton icon={copiedToken === convite.token ? Check : Copy} label={t('inviteManager.copyCode')} onClick={() => copiarToken(convite.token)} className={copiedToken === convite.token ? 'text-verde-600' : ''} />
                    </div>
                    <p className="text-xs text-gray-400 mt-2">{t('inviteManager.createdOn', { date: formatDateWithMonthName(convite.criado_em, lang) })}</p>
                  </div>
                  <IconButton icon={Trash2} label={t('inviteManager.removeInvite')} tone="danger" onClick={() => removerConvite(convite.id)} />
                </div>
                <Button variant="secondary" size="sm" className="mt-3 w-full sm:w-auto" onClick={() => copiarMensagemCompleta(convite.email, convite.token)}>
                  {t('inviteManager.copyFullMessage')}
                </Button>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Notice>
        <span dangerouslySetInnerHTML={{ __html: t('inviteManager.howItWorks') }} />
      </Notice>
    </div>
  );
}
